import { getDateWithTimezoneFixed } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { differenceInYears } from 'date-fns';
import _ from 'lodash';
import { TGiftPackType } from '../../../entities/giftPackages/queries';
import { productCustomsFields } from '../../../entities/products/shared';
import { isDependant } from '../../../entities/recipients/shared';
import { TShipmentType } from '../../../entities/shipments/queries';
import { TDependantType, TEventTypes, TGiftPackageType, TGiftTypes, TProduct, TWorkAnniversaryGiftVariations } from '../../../generated/graphql';
import { getPackageProducts } from './getProducts';

type TGetDefaultCustomsProps = {
  shipment?: null | TShipmentType;
  defaultPackages?: null | TGiftPackType[];
};

export const getDefaultCustoms = ({ shipment, defaultPackages }: TGetDefaultCustomsProps) => {
  const allProducts: TProduct[] = [];
  if (!shipment) return allProducts;

  const company = shipment.company;
  const giftPacks = (shipment.giftPacks as unknown as TGiftPackType[]) || [];

  //console.log('shipment:', shipment);
  //console.log('defaultPackages:', defaultPackages);
  //console.log('company:', company);
  //console.log('giftPacks:', giftPacks);

  const packageConfigByGiftType: Partial<Record<TGiftTypes, { type: TGiftPackageType; subType?: string; activated?: boolean | null }>> = {
    [TGiftTypes.NewHire]: {
      type: TGiftPackageType.NewHire,
      activated: company?.employeeNewHireKitActivated
    },
    [TGiftTypes.WorkAnniversary]: {
      type: TGiftPackageType.WorkAnniversary,
      subType: company?.workAnniversaryGiftVariation,
      activated: company?.employeeWorkAnniversaryActivated
    },
    [TGiftTypes.CustomBricks]: {
      type: TGiftPackageType.WorkAnniversary,
      subType: TWorkAnniversaryGiftVariations.BrickOnly,
      activated: company?.customBricksActivated
    },
    [TGiftTypes.Birthday]: {
      type: TGiftPackageType.EmployeeBirthday,
      activated: company?.employeeBirthdayActivated
    },
    // --- new one-off subtypes ---
    [TGiftTypes.BabyGift]: { type: TGiftPackageType.OneOff, subType: TEventTypes.BabyGift, activated: true },
    [TGiftTypes.Bereavement]: { type: TGiftPackageType.OneOff, subType: TEventTypes.Bereavement, activated: true },
    [TGiftTypes.CarePackage]: { type: TGiftPackageType.OneOff, subType: TEventTypes.CarePackage, activated: true },
    [TGiftTypes.Wedding]: { type: TGiftPackageType.OneOff, subType: TEventTypes.Wedding, activated: true },
    [TGiftTypes.ReplacementWorkAnniversary]: {
      type: TGiftPackageType.OneOff,
      subType: TEventTypes.ReplacementWorkAnniversary,
      activated: true
    },
    [TGiftTypes.ReplacementEmployeeBirthday]: {
      type: TGiftPackageType.OneOff,
      subType: TEventTypes.ReplacementEmployeeBirthday,
      activated: true
    }
  };

  const productsByGiftType = _.mapValues(packageConfigByGiftType, (config) => (config ? getPackageProducts({ ...config, defaultPackages, giftPacks }) : []));

  const spouseBirthdayProducts = getPackageProducts({
    activated: company?.significantOtherBirthdayActivated,
    type: TGiftPackageType.SpouseBirthday,
    defaultPackages,
    giftPacks
  });

  for (const gift of shipment.gifts?.items || []) {
    if (!gift) continue;
    const recipient = gift.recipient;
    const giftProducts: TProduct[] = [];

    switch (gift.type) {
      case TGiftTypes.NewHire:
        giftProducts.push(...(productsByGiftType.newHire as TProduct[]));
        break;

      case TGiftTypes.WorkAnniversary:
        giftProducts.push(...(productsByGiftType.workAnniversary as TProduct[]));
        break;

      case TGiftTypes.Birthday:
        if (recipient)
          if (!isDependant(recipient)) giftProducts.push(...(productsByGiftType.birthday as TProduct[]));
          else if (recipient.type == TDependantType.SignificantOther) giftProducts.push(...spouseBirthdayProducts);
          else if (recipient.type == TDependantType.Child) {
            const birthDate = getDateWithTimezoneFixed(recipient.birthDate);
            const childYearsDiff = birthDate ? differenceInYears(new Date(), birthDate) : NaN;
            switch (true) {
              case childYearsDiff >= 1 && childYearsDiff <= 9:
                giftProducts.push(...((defaultPackages?.find((pack) => pack.type == TGiftPackageType.ChildBirthday && pack.subType == `child${childYearsDiff}`)?.hydratedProducts as unknown as TProduct[]) || []));
                break;
              case childYearsDiff >= 10 && childYearsDiff <= 14:
                giftProducts.push(...((defaultPackages?.find((pack) => pack.type == TGiftPackageType.ChildBirthday && pack.subType == `child10to14`)?.hydratedProducts as unknown as TProduct[]) || []));
                break;
              case childYearsDiff >= 15 && childYearsDiff <= 18:
                giftProducts.push(...((defaultPackages?.find((pack) => pack.type == TGiftPackageType.ChildBirthday && pack.subType == `child15to18`)?.hydratedProducts as unknown as TProduct[]) || []));
                break;
            }
          }

        break;

      case TGiftTypes.CustomBricks:
        giftProducts.push(...(productsByGiftType.customBricks as TProduct[]));
        break;
      case TGiftTypes.BabyGift:
        giftProducts.push(...(productsByGiftType.babyGift as TProduct[]));
        break;
      case TGiftTypes.Bereavement:
        giftProducts.push(...(productsByGiftType.bereavement as TProduct[]));
        break;
      case TGiftTypes.CarePackage:
        giftProducts.push(...(productsByGiftType.carePackage as TProduct[]));
        break;
      case TGiftTypes.Wedding:
        giftProducts.push(...(productsByGiftType.wedding as TProduct[]));
        break;
      case TGiftTypes.ReplacementWorkAnniversary:
        giftProducts.push(...(productsByGiftType.replacementWorkAnniversary as TProduct[]));
        break;
      case TGiftTypes.ReplacementEmployeeBirthday:
        giftProducts.push(...(productsByGiftType.replacementEmployeeBirthday as TProduct[]));
        break;
    }
    // Each gift contributes its brick count to every item in its applied package.
    allProducts.push(...giftProducts.map((product) => ({ ...product, quantity: gift.brickQuantity ?? 1 })));
  }

  return _.map(_.groupBy(allProducts, 'id'), (group) =>
    _.pick(
      {
        ...group[0],
        quantity: _.sumBy(group, 'quantity')
        //value: _.sumBy(group, 'value'),
      },
      productCustomsFields
    )
  );
};
